import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import "./ReceptionStyles.css";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../hooks/useAuth";
import { useClinicData } from "../../hooks/useClinicData";
import { USER_TYPES } from "../../constants/userTypes";
import AppointmentsTab from "../../components/reception/AppointmentsTab";
import OutpatientTab from "../../components/reception/OutpatientTab";
import InpatientTab from "../../components/reception/InpatientTab";
import EmergencyQueue from "../../components/reception/EmergencyQueue";
import Billing from "../medical/Billing.jsx";
import PosInvoiceModal from "../../components/modals/PosInvoiceModal";
import "flatpickr/dist/flatpickr.min.css";
import { useNavigate, useParams } from "react-router-dom";
import { useFlatpickrUserType } from "../../hooks/useFlatpickrUserType";


// Sulhafa-style header
const ReceptionHeader = ({ activeTab, setActiveTab, userType, isOwner, tabSwitching = false, onNewInvoice }) => {
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const { t, i18n } = useTranslation();

  // Handle keyboard events for modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isLogoutModalOpen) {
        setIsLogoutModalOpen(false);
      }
    };

    if (isLogoutModalOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isLogoutModalOpen]);

  const handleLogoutClick = () => {
    setIsLogoutModalOpen(true);
  };


  const handleLogout = async () => {
    try {
      setIsLogoutModalOpen(false);
      localStorage.clear();
      sessionStorage.clear();
      document.cookie.split(";").forEach(function (c) {
        if (c.trim().startsWith("sulhafa_")) return;
        document.cookie = c
          .replace(/^ +/, "")
          .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
      });
      window.location.replace("/auth/login");
    } catch (error) {
      setIsLogoutModalOpen(false);
      window.location.replace("/auth/login");
    }
  };

  // Handle tab switching with loading state
  const handleTabSwitch = (newTab) => {
    if (activeTab === newTab) return; // Don't switch if already active
    setActiveTab(newTab);
  };


  // Helper to get tab from URL
  function getTabFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get("tab");
    if (tab === "appointments" || tab === "outpatient" || tab === "inpatient" || tab === "billing") {
      return tab;
    }
    return null;
  }

  useEffect(() => {
    const tabFromUrl = getTabFromUrl();
    if (tabFromUrl) {
      setActiveTab(tabFromUrl);
    }
    // Strip the deep-link flag so a refresh doesn't reopen the modal
    if (new URLSearchParams(window.location.search).get("new")) {
      const url = new URL(window.location.href);
      url.searchParams.delete("new");
      window.history.replaceState({}, "", url);
    }
    // Only run on mount
    // eslint-disable-next-line
  }, []);



  return (
    <>
      <header
        className="reception-main-header reception-main-header-responsive"
        dir={i18n.dir()}
        lang={i18n.language}
      >
        <div className="reception-header-logo reception-header-logo-responsive">
          {userType === USER_TYPES.CLINIC_MODERATOR ? (
            <div
              style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
              onClick={() => {
                const ut = getUserType();
                if (ut === 'ADMIN' || ut === 'CONTENT_ADMIN') {
                  return navigate('/admin/dashboard');
                }
                navigate("/social");
              }}
            >
              <img
                src="/Landing page assets/Sulhafa_Logo.png"
                alt="Sulhafa Logo"
                className="reception-logo-img reception-logo-icon"
                style={{ height: "70px", width: "auto", display: "block" }}
              />
              <img
                src="/Landing page assets/Sulhafa_Logo_Text.png"
                alt="Sulhafa"
                className="reception-logo-img reception-logo-text"
                style={{ height: "52px", width: "auto", display: "block" }}
              />
            </div>
          ) : (
            <span></span>
          )}
        </div>
        {userType !== USER_TYPES.VETERINARIAN && (
          <nav className="reception-header-nav reception-header-nav-responsive">
            {(userType === USER_TYPES.CLINIC_MODERATOR || isOwner) && (
              <a
                href="#"
                className={`reception-header-link${activeTab === "appointments" ? " active" : ""}${tabSwitching && activeTab === "appointments" ? " loading" : ""}`}
                onClick={() => handleTabSwitch("appointments")}
              >
                {t("reception.header.tabs.appointments")}
                {tabSwitching && activeTab === "appointments" && (
                  <div className="tab-loading-spinner" aria-hidden="true"></div>
                )}
              </a>
            )}
            <a
              href="#"
              className={`reception-header-link${activeTab === "outpatient" ? " active" : ""}${tabSwitching && activeTab === "outpatient" ? " loading" : ""}`}
              onClick={() => handleTabSwitch("outpatient")}
            >
              {t("reception.header.tabs.outpatient")}
              {tabSwitching && activeTab === "outpatient" && (
                <div className="tab-loading-spinner" aria-hidden="true"></div>
              )}
            </a>
            <a
              href="#"
              className={`reception-header-link${activeTab === "inpatient" ? " active" : ""}${tabSwitching && activeTab === "inpatient" ? " loading" : ""}`}
              onClick={() => handleTabSwitch("inpatient")}
            >
              {t("reception.header.tabs.inpatient")}
              {tabSwitching && activeTab === "inpatient" && (
                <div className="tab-loading-spinner" aria-hidden="true"></div>
              )}
            </a>
            {(userType === USER_TYPES.CLINIC_MODERATOR || isOwner) && (
              <a
                href="#"
                className={`reception-header-link${activeTab === "billing" ? " active" : ""}${tabSwitching && activeTab === "billing" ? " loading" : ""}`}
                onClick={() => handleTabSwitch("billing")}
              >
                {t("reception.header.tabs.billing", "Billing")}
                {tabSwitching && activeTab === "billing" && (
                  <div className="tab-loading-spinner" aria-hidden="true"></div>
                )}
              </a>
            )}
          </nav>
        )}
        <div className="reception-header-actions reception-header-actions-responsive">
          {userType !== USER_TYPES.VETERINARIAN && onNewInvoice && (userType === USER_TYPES.CLINIC_MODERATOR || isOwner) && (
            <button
              type="button"
              className="reception-header-new-invoice"
              onClick={onNewInvoice}
              style={{
                padding: "8px 18px",
                marginInlineEnd: "12px",
                borderRadius: "8px",
                border: "2px solid #00D4B3",
                backgroundColor: "transparent",
                color: "#00A896",
                fontSize: "0.85rem",
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "inherit",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#F0FDFA";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              {t("posInvoice.newInvoiceButton", "Walk-In Invoice")}
            </button>
          )}
          {userType !== USER_TYPES.VETERINARIAN && (
            <button
              className="reception-header-logout reception-header-logout-responsive"
              onClick={handleLogoutClick}
            >
              {t("reception.header.logout")}
            </button>
          )}
        </div>
      </header>

      {isLogoutModalOpen && (
        <div
          className="reception-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsLogoutModalOpen(false);
            }
          }}
        >
          <div className="reception-modal">
            <div className="reception-modal-header">
              <span className="reception-modal-title">
                {t("reception.header.confirmLogout.title")}
              </span>
              <button
                className="reception-modal-close"
                onClick={() => setIsLogoutModalOpen(false)}
              >
                &times;
              </button>
            </div>
            <div
              style={{
                marginBottom: 24,
                fontSize: "1rem",
                color: "#303e4e",
                display: "flex",
                alignItems: "center",
                gap: "12px"
              }}
            >
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  backgroundColor: "#FEF2F2",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0
                }}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#EF4444"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16,17 21,12 16,7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
              </div>
              <div>
                <div style={{ fontWeight: "600", marginBottom: "4px" }}>
                  {t("reception.header.confirmLogout.question")}
                </div>
                <div style={{ fontSize: "0.875rem", color: "#64748b" }}>
                  {t("reception.header.confirmLogout.subtitle")}
                </div>
              </div>
            </div>
            <div className="reception-modal-actions">
              <button
                type="button"
                className="reception-action-btn reception-modal-cancel"
                onClick={() => setIsLogoutModalOpen(false)}
              >
                {t("reception.header.confirmLogout.cancel")}
              </button>
              <button
                type="button"
                className="reception-action-btn"
                onClick={handleLogout}
                style={{
                  background: "linear-gradient(135deg, #EF4444, #DC2626)",
                  color: "#fff",
                  boxShadow: "0 4px 12px rgba(239, 68, 68, 0.3)"
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = "linear-gradient(135deg, #DC2626, #B91C1C)";
                  e.target.style.transform = "translateY(-1px)";
                  e.target.style.boxShadow = "0 6px 16px rgba(239, 68, 68, 0.4)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "linear-gradient(135deg, #EF4444, #DC2626)";
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = "0 4px 12px rgba(239, 68, 68, 0.3)";
                }}
              >
                {t("reception.header.confirmLogout.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const ROWS_PER_PAGE = 10;

// Main Reception Page
const MainReceptionPage = ({ clinicData, locationState, tabType }) => {
  // Apply user type color to Flatpickr calendars globally
  useFlatpickrUserType();
  const { t, i18n } = useTranslation();

  // Check authentication immediately - if no user data (during logout), redirect instantly
  const isAuthenticated = () => {
    const token = localStorage.getItem('access_token');
    const userData = localStorage.getItem('user_data');
    return !!(token && userData && userData !== 'null' && userData !== 'undefined');
  };

  // Early return if not authenticated (prevents flash during logout)
  if (!isAuthenticated()) {
    return null;
  }

  // For guardian search and pet dropdown
  const [loadingLists, setLoadingLists] = useState(true);
  const [activeTab, setActiveTab] = useState(tabType || "appointments");
  const [tabSwitching, setTabSwitching] = useState(false);
  const [isManualSwitch, setIsManualSwitch] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const closeDetailsRef = useRef(null);
  const [isSOAPOpen, setIsSOAPOpen] = useState(false);
  const closeSOAPRef = useRef(null);
  const [showPosInvoiceModal, setShowPosInvoiceModal] = useState(false);
  // Deep-link from the Clinic Operations hub (?new=1): the active tab opens
  // its create modal on mount, then the flag is consumed.
  const [autoOpenCreate, setAutoOpenCreate] = useState(
    () => new URLSearchParams(window.location.search).get("new") === "1"
  );

  // Handler to close details from parent
  const handleCloseDetails = useCallback(() => {
    // If SOAP is open, close SOAP first (check ref directly to avoid stale state)
    if (closeSOAPRef.current) {
      closeSOAPRef.current();
      // Don't close details view - just close SOAP
      return;
    }
    // Otherwise, close details view
    if (closeDetailsRef.current) {
      closeDetailsRef.current();
    }
    setIsDetailsOpen(false);
    closeDetailsRef.current = null;
  }, []);

  // Sync activeTab when tabType prop changes (e.g., from URL navigation)
  // Only sync if it wasn't a manual switch
  useEffect(() => {
    if (isManualSwitch) {
      // Reset flag after a brief delay to allow navigation to complete
      const timer = setTimeout(() => {
        setIsManualSwitch(false);
      }, 100);
      return () => clearTimeout(timer);
    }

    // Only sync from URL if it's not a manual switch
    if (!isManualSwitch && tabType && tabType !== activeTab) {
      setActiveTab(tabType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabType, isManualSwitch]);
  const { user, getUserType } = useAuth();
  const userType = getUserType();
  // Remove clinics state, use only current clinic
  const [clinic, setClinic] = useState(null);
  const [vets, setVets] = useState([]);
  // Track if we've already processed the clinicData to prevent duplicate calls
  const hasProcessedData = useRef(false);

  // Centralized data state for all tabs
  const [allTabData, setAllTabData] = useState({
    appointments: [],
    outpatientVisits: [],
    inpatientVisits: [],
    loading: true,
    error: null,
  });
  const [hasFetchedAllData, setHasFetchedAllData] = useState(false);

  // Use centralized data loading hook for CLINIC_MODERATOR
  const {
    data: clinicDataFromHook,
    loading: clinicDataLoading,
    error: clinicDataError,
    getClinicId,
    isOwner,
    isMember,
    getOwnedClinics,
  } = useClinicData();

  // Helper function to check if user owns any clinic
  const hasOwnedClinics = () => {
    const clinics = getOwnedClinics();
    return clinics && Array.isArray(clinics) && clinics.length > 0;
  };

  // Handle tab switching with loading state
  const handleTabSwitch = (newTab) => {
    if (activeTab === newTab) return; // Don't switch if already active

    setIsManualSwitch(true);
    setTabSwitching(true);
    setActiveTab(newTab);

    // Brief loading state for visual feedback
    setTimeout(() => {
      setTabSwitching(false);
    }, 300);
  };

  // Keep the tab in sync with the URL (so user can share/bookmark the tab)
  const navigate = useNavigate();
  const params = useParams();
  const urlClinicId = params?.clinicId;
  // Get clinicId from URL params or hook for use throughout component
  const clinicId = urlClinicId || getClinicId();
  useEffect(() => {
    // Only update URL if activeTab changed and differs from current tabType
    // Skip if it's the initial render and tabType already matches
    if (activeTab && tabType !== activeTab) {
      // Get clinicId from URL params or hook
      const clinicId = urlClinicId || getClinicId();
      let basePath = clinicId
        ? `/medical/clinic/${clinicId}/appointment-management`
        : "/medical/clinic/appointment-management";

      // If locationState has outpatientId/inpatientId, preserve it only for that specific tab
      if (locationState && locationState.outpatientId && activeTab === "outpatient") {
        basePath = clinicId
          ? `/medical/clinic/${clinicId}/appointment-management/outpatient/${locationState.outpatientId}`
          : `/medical/clinic/appointment-management/outpatient/${locationState.outpatientId}`;
      } else if (locationState && locationState.inpatientId && activeTab === "inpatient") {
        basePath = clinicId
          ? `/medical/clinic/${clinicId}/appointment-management/inpatient/${locationState.inpatientId}`
          : `/medical/clinic/appointment-management/inpatient/${locationState.inpatientId}`;
      } else {
        // Normal navigation without IDs
        basePath = clinicId
          ? `/medical/clinic/${clinicId}/appointment-management/${activeTab}`
          : `/medical/clinic/appointment-management/${activeTab}`;
      }
      navigate(basePath, { replace: true });
    }
    // eslint-disable-next-line
  }, [activeTab, urlClinicId, getClinicId]);

  // No longer redirecting away from appointments for non-owners/vets
  // Logic removed to allow everyone access to Appointments tab
  // useEffect(() => { ... }, [...]);

  // Process clinicData using useMemo to prevent duplicate processing
  const processedData = useMemo(() => {
    // Use appropriate clinicData source based on user type
    const dataToUse =
      userType === USER_TYPES.CLINIC_MODERATOR
        ? clinicDataFromHook || clinicData
        : clinicData;

    if (!dataToUse || dataToUse === null) {
      return null;
    }

    return {
      clinic: dataToUse.clinic || null,
      clinicMembers: dataToUse.clinic_members || [],
      allVets:
        dataToUse.clinic_members.reduce((acc, member) => {
          if (member.role === "doctor") {
            acc.push(member);
          }
          return acc;
        }, []) || [],
    };
  }, [clinicData, clinicDataFromHook, userType]);

  // Use processed data to set state
  useEffect(() => {
    if (processedData && !hasProcessedData.current) {
      setClinic(processedData.clinic);
      setVets(processedData.allVets);
      setLoadingLists(false);
      hasProcessedData.current = true;
    }
  }, [processedData]);

  // Centralized data fetching function
  const fetchAllTabDataInFlightRef = useRef(false);
  const fetchAllTabData = async (forceRefresh = false) => {
    if (hasFetchedAllData && !forceRefresh) return; // Prevent duplicate calls unless forced
    // The effect can re-run while the first fetch is still in flight
    // (processedData identity changes) — never fire a second request.
    if (fetchAllTabDataInFlightRef.current) return;
    fetchAllTabDataInFlightRef.current = true;

    try {

    const token = localStorage.getItem("access_token");
    const endpoint = "/api/v1/clinic-appointments/current-day";

    // Get clinic_id from useClinicData hook (reads from URL params)
    const clinicId = getClinicId();

    // Build URL with clinic_id query parameter
    const url = clinicId
      ? `${import.meta.env.VITE_API_URL}${endpoint}?clinic_id=${clinicId}`
      : `${import.meta.env.VITE_API_URL}${endpoint}`;

    setAllTabData((prev) => ({ ...prev, loading: true, error: null }));

    try {
      // Fetch current day appointments - with error handling for backend issues
      let appointments = [];
      try {
        const response = await fetch(
          url,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              accept: "application/json",
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          appointments = data?.data?.appointments || [];
        } else {
          // If API fails, log error but continue with empty array
          const errorData = await response.json().catch(() => ({}));
          console.warn('Current-day API error:', errorData.message || 'Failed to fetch current day data');
          appointments = [];
        }
      } catch (apiError) {
        // Gracefully handle API errors - log but don't break the app
        console.warn('Error fetching current-day data:', apiError);
        appointments = [];
      }

      // Filter data by type
      const appointmentsOnly = appointments.filter(
        (appt) => appt.type === "appointment"
      );
      const outpatientOnly = appointments.filter(
        (appt) => appt.type === "outpatient_visit"
      );
      const inpatientOnly = appointments.filter(
        (appt) => appt.type === "inpatient_visit"
      );

      // Process appointments data
      const processedAppointments = appointmentsOnly.map((appt) => {
        const date = appt.appointment_at ? appt.appointment_at.split('T')[0].split(' ')[0] : '';
        const time = appt.appointment_at || ''; // Preserve full string for shift-aware parsing in child components

        return {
          date: date,
          time: time,
          pet_owner:
            appt.pet_owner &&
              (appt.pet_owner.first_name || appt.pet_owner.last_name)
              ? `${appt.pet_owner.first_name || ""} ${appt.pet_owner.last_name || ""
                }`.trim()
              : "Unknown Owner",
          pet: appt.pet?.name || "",
          vet:
            appt.vet && (appt.vet.first_name || appt.vet.last_name)
              ? `${appt.vet.first_name || ""} ${appt.vet.last_name || ""
                }`.trim()
              : "Unknown Vet",
          reason: appt.reason || "",
          id: appt.id,
          status: appt.status || "pending",
          type: "appointment",
          originalData: {
            id: appt.id,
            type: "appointment",
            pet_owner_id: appt.pet_owner?.id || null,
            pet_id: appt.pet?.id || null,
            vet_id: appt.vet?.id || null,
            pet_owner:
              appt.pet_owner &&
                (appt.pet_owner.first_name || appt.pet_owner.last_name)
                ? `${appt.pet_owner.first_name || ""} ${appt.pet_owner.last_name || ""
                  }`.trim()
                : "Unknown Owner",
            pet: appt.pet,
            vet: appt.vet,
            reason: appt.reason || "",
            date: date,
            time: time,
            status: appt.status || "pending",
            appointment_type_id: appt.appointment_type_id || "",
            appointment_type: appt.appointment_type || "",
            pet_owner: appt.pet_owner,
            user: appt.user,
            clinic: appt.clinic,
            notes: appt.notes,
            canceled_reason: appt.canceled_reason,
            canceled_by: appt.canceled_by,
            restored_by: appt.restored_by,
            restored_at: appt.restored_at,
            canceled_at: appt.canceled_at,
            created_at: appt.created_at,
            updated_at: appt.updated_at,
            appointment_at: appt.appointment_at || "",
          },
        };
      });

      // Process outpatient visits data
      const processedOutpatientVisits = outpatientOnly.map((visit) => {
        const date = visit.visit_date ? visit.visit_date.split('T')[0].split(' ')[0] : '';
        const time = visit.visit_date || '';
        visit.pet.profile_image_path =
          import.meta.env.VITE_API_URL + "/" + visit.pet.profile_image_path;

        return {
          date: date,
          time: time,
          pet_owner:
            visit.pet_owner &&
              (visit.pet_owner.first_name || visit.pet_owner.last_name)
              ? `${visit.pet_owner.first_name || ""} ${visit.pet_owner.last_name || ""
                }`.trim()
              : "Unknown Owner",
          pet: {
            profile_image_path: visit.pet.profile_image_path,
            name: visit.pet.name,
          },
          vet:
            visit.vet && (visit.vet.first_name || visit.vet.last_name)
              ? `${visit.vet.first_name || ""} ${visit.vet.last_name || ""
                }`.trim()
              : "Unknown Vet",
          reason: visit.reason || "",
          id: visit.id,
          status: visit.status || "pending",
          type: "outpatient_visit",
          out_serial_num: visit.out_serial_num || "",
          downpayment: visit.downpayment || 0,
          payments_note: visit.payments_note || "",
          payment_type_id: visit.payment_type_id || null,
          remaining_amount: visit.remaining_amount || 0,
          total_amount: visit.total_amount || 0,

          originalData: {
            id: visit.id,
            type: "outpatient_visit",
            pet_owner_id: visit.pet_owner?.id || null,
            pet_id: visit.pet?.id || null,
            pet: {
              profile_image_path: visit.pet.profile_image_path,
              name: visit.pet.name,
            },
            vet_id: visit.vet?.id || null,
            pet_owner:
              visit.pet_owner &&
                (visit.pet_owner.first_name || visit.pet_owner.last_name)
                ? `${visit.pet_owner.first_name || ""} ${visit.pet_owner.last_name || ""
                  }`.trim()
                : "Unknown Owner",
            pet_id: visit.pet?.id || null, // EXPOSE PET ID for filtering
            pet: {
              id: visit.pet.id,
              profile_image_path: visit.pet.profile_image_path,
              name: visit.pet.name,
            },
            vet: visit.vet,
            reason: visit.reason || "",
            date: date,
            time: time,
            status: visit.status || "pending",
            pet_owner: visit.pet_owner,
            user: visit.user,
            clinic: visit.clinic,
            notes: visit.notes,
            out_serial_num: visit.out_serial_num || "",
            visit_date: visit.visit_date || "",
            downpayment: visit.downpayment || 0,
            created_at: visit.created_at,
            updated_at: visit.updated_at,
            remaining_amount: visit.remaining_amount || 0,
            total_amount: visit.total_amount || 0,
          },
        };
      });

      // Process inpatient visits data
      const processedInpatientVisits = inpatientOnly.map((visit) => {
        const date = visit.visit_date ? visit.visit_date.split('T')[0].split(' ')[0] : '';
        const time = visit.visit_date || '';

        // Ensure pet image has absolute path like outpatient
        if (visit.pet && visit.pet.profile_image_path) {
          try {
            const imagePath = visit.pet.profile_image_path;
            const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
            // Handle paths that might or might not start with /
            const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
            visit.pet.profile_image_path = `${API_BASE_URL}${cleanPath}`;
          } catch (_) { }
        }
        return {
          date: date,
          time: time,
          pet_owner:
            visit.pet_owner &&
              (visit.pet_owner.first_name || visit.pet_owner.last_name)
              ? `${visit.pet_owner.first_name || ""} ${visit.pet_owner.last_name || ""
                }`.trim()
              : "Unknown Owner",
          pet_id: visit.pet?.id || null, // EXPOSE PET ID for filtering
          pet: visit.pet
            ? { id: visit.pet.id, profile_image_path: visit.pet.profile_image_path, name: visit.pet.name }
            : (visit.pet?.name || ""),
          vet:
            visit.vet && (visit.vet.first_name || visit.vet.last_name)
              ? `${visit.vet.first_name || ""} ${visit.vet.last_name || ""
                }`.trim()
              : "Unknown Vet",
          reason: visit.reason || "",
          id: visit.id,
          status: visit.status || "admitted",
          type: "inpatient_visit",
          cage_no: visit.cage_no || "",
          downpayment: visit.downpayment || 0,
          payments_note: visit.payments_note || "",
          in_serial_num: visit.in_serial_num || "",
          payment_type_id: visit.payment_type_id || null,
          allowed_food: visit.feeding_order || visit.allowed_food || "",
          allowed_fluids: visit.water_order || visit.allowed_fluids || "",
          rate_per_day: visit.rate_per_day || 0,
          is_isolated: visit.is_isolated || false,
          length_of_stay_days: typeof visit.length_of_stay_days === 'number' ? visit.length_of_stay_days : null,
          billable_stay_days: typeof visit.billable_stay_days === 'number' ? visit.billable_stay_days : null,
          remaining_amount: visit.remaining_amount || 0,
          total_amount: visit.total_amount || 0,
          originalData: {
            id: visit.id,
            type: "inpatient_visit",
            pet_owner_id: visit.pet_owner?.id || null,
            pet_id: visit.pet?.id || null,
            vet_id: visit.vet?.id || null,
            pet_owner: visit.pet_owner,
            pet: visit.pet
              ? { profile_image_path: visit.pet.profile_image_path, name: visit.pet.name }
              : visit.pet,
            vet: visit.vet,
            reason: visit.reason || "",
            date: date,
            time: time,
            status: visit.status || "admitted",
            cage_no: visit.cage_no || "",
            in_serial_num: visit.in_serial_num || "",
            payment_type_id: visit.payment_type_id || null,
            downpayment: visit.downpayment || 0,
            allowed_food: visit.feeding_order || visit.allowed_food || "",
            allowed_fluids: visit.water_order || visit.allowed_fluids || "",
            rate_per_day: visit.rate_per_day || 0,
            is_isolated: visit.is_isolated || false,
            length_of_stay_days: typeof visit.length_of_stay_days === 'number' ? visit.length_of_stay_days : null,
            billable_stay_days: typeof visit.billable_stay_days === 'number' ? visit.billable_stay_days : null,
            visit_date: visit.visit_date || "",
            created_at: visit.created_at,
            updated_at: visit.updated_at,
            remaining_amount: visit.remaining_amount || 0,
            total_amount: visit.total_amount || 0,
          },
        };
      });

      setAllTabData({
        appointments: processedAppointments,
        outpatientVisits: processedOutpatientVisits,
        inpatientVisits: processedInpatientVisits,
        loading: false,
        error: null,
      });

      setHasFetchedAllData(true);
    } catch (error) {
      console.error("Error fetching all tab data:", error);
      setAllTabData((prev) => ({
        ...prev,
        loading: false,
        error: error.message || "Failed to fetch data",
      }));
    }
    } finally {
      fetchAllTabDataInFlightRef.current = false;
    }
  };

  // Fetch all data when component mounts and clinic data is available
  useEffect(() => {
    if (processedData && !hasFetchedAllData) {
      fetchAllTabData();
    }
  }, [processedData, hasFetchedAllData]);

  // Add class to body to disable scrolling only on reception page
  // Removed to allow tabs to be scrollable
  // useEffect(() => {
  //   document.body.classList.add('reception-page-active');
  //   
  //   return () => {
  //     document.body.classList.remove('reception-page-active');
  //   };
  // }, []);

  // Handle CLINIC_MODERATOR user type with clinic data loading
  if (userType === USER_TYPES.CLINIC_MODERATOR) {
    // Show loading state
    if (clinicDataLoading) {
      return (
        <div className="reception-main-layout">
          <ReceptionHeader
            activeTab={activeTab}
            setActiveTab={handleTabSwitch}
            userType={user?.user_type}
            tabSwitching={tabSwitching}
          />
          <div className="reception-main-body">
            <div
              style={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
                minHeight: "400px",
              }}
            >
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  border: "4px solid #f3f3f3",
                  borderTop: "4px solid #3498db",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                }}
              ></div>
              <p style={{ marginTop: "16px", color: "#666", fontSize: "16px" }}>
                {t("reception.loadingClinicData")}
              </p>
            </div>
          </div>
        </div>
      );
    }

    // Show error state
    if (clinicDataError) {
      return (
        <div className="reception-main-layout">
          <ReceptionHeader
            activeTab={activeTab}
            setActiveTab={handleTabSwitch}
            userType={user?.user_type}
            tabSwitching={tabSwitching}
          />
          <div className="reception-main-body">
            <div
              style={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
                minHeight: "400px",
              }}
            >
              <div
                style={{
                  color: "#e53935",
                  fontSize: "18px",
                  marginBottom: "16px",
                }}
              >
                {t("reception.error.title")}
              </div>
              <p
                style={{ color: "#666", fontSize: "16px", textAlign: "center" }}
              >
                {clinicDataError}
              </p>
              <button
                onClick={() => window.location.reload()}
                style={{
                  marginTop: "16px",
                  padding: "8px 16px",
                  backgroundColor: "#3498db",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                {t("reception.error.retry")}
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Use clinicData from hook for CLINIC_MODERATOR
    const clinicDataToUse = clinicDataFromHook || clinicData;
  }

  // Final check before rendering - if somehow we got here without proper auth, return null
  if (!isAuthenticated()) {
    return null;
  }

  return (
    <div
      className="reception-main-layout reception-main-layout-responsive"
      dir={i18n.dir()}
      lang={i18n.language}
    >
      {/* Hide header for Veterinarian; Clinic Moderator handled in earlier return */}
      {userType !== USER_TYPES.VETERINARIAN && (
        <ReceptionHeader
          activeTab={activeTab}
          setActiveTab={handleTabSwitch}
          userType={user?.user_type}
          isOwner={typeof isOwner === 'function' ? isOwner() : !!isOwner}
          tabSwitching={tabSwitching}
          onNewInvoice={() => setShowPosInvoiceModal(true)}
        />
      )}
      {/* Vet-only back to hub */}
      {userType === USER_TYPES.VETERINARIAN && (
        <div className="reception-backbar" dir={i18n.dir()} lang={i18n.language}>
          {isDetailsOpen ? (
            <button
              type="button"
              onClick={handleCloseDetails}
              className="reception-backbtn"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '8px' }}>
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              {isSOAPOpen ? t("reception.backBar.backToDetails") : t("reception.backBar.back")}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => navigate(`/medical/clinic/${clinicId}/appointment-management`)}
              className="reception-backbtn"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '8px' }}>
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              {t("reception.backBar.backToOperations")}
            </button>
          )}
        </div>
      )}
      <div className="reception-main-body reception-main-body-responsive">
        <main className="reception-main-content reception-main-content-responsive">
          {/* Only render the active tab component to prevent duplicate API calls */}
          {activeTab === "appointments" && (
            <>
              {(userType === USER_TYPES.CLINIC_MODERATOR ||
                userType === USER_TYPES.VETERINARIAN ||
                (typeof isOwner === 'function' ? isOwner() : !!isOwner)) ? (
                <AppointmentsTab
                  clinicData={clinicData}
                  clinicDataFromHook={clinicDataFromHook}
                  userType={userType}
                  vets={vets}
                  loadingLists={allTabData.loading}
                  appointments={allTabData.appointments}
                  autoOpenCreate={autoOpenCreate}
                  onAutoOpenCreateConsumed={() => setAutoOpenCreate(false)}
                  fetchAllTabData={fetchAllTabData}
                  onDataUpdate={(newData) => {
                    setAllTabData((prev) => ({
                      ...prev,
                      appointments: newData,
                    }));
                  }}
                />
              ) : (
                <div style={{ padding: "40px", textAlign: "center" }}>
                  <p style={{ color: "#666", fontSize: "16px" }}>
                    {t("reception.noPermission.message")}
                  </p>
                  <button
                    onClick={() => setActiveTab("outpatient")}
                    style={{
                      marginTop: "16px",
                      padding: "8px 16px",
                      backgroundColor: "#3498db",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                    }}
                  >
                    {t("reception.noPermission.cta")}
                  </button>
                </div>
              )}
            </>
          )}

          {activeTab === "emergency" && (
            <EmergencyQueue />
          )}

          {activeTab === "outpatient" && (
            <OutpatientTab
              clinicData={clinicData}
              clinicDataFromHook={clinicDataFromHook}
              userType={userType}
              isOwner={typeof isOwner === 'function' ? isOwner() : !!isOwner}
              vets={vets}
              loadingLists={allTabData.loading}
              outpatientVisits={allTabData.outpatientVisits}
              inpatientVisits={allTabData.inpatientVisits}
              autoOpenCreate={autoOpenCreate}
              onAutoOpenCreateConsumed={() => setAutoOpenCreate(false)}
              fetchAllTabData={fetchAllTabData}
              onDataUpdate={(newData) => {
                setAllTabData((prev) => ({
                  ...prev,
                  outpatientVisits: newData,
                }));
              }}
              onDetailsOpenChange={(isOpen, closeHandler, isSOAPOpen, closeSOAPHandler) => {
                setIsDetailsOpen(isOpen);
                if (isOpen && closeHandler) {
                  closeDetailsRef.current = closeHandler;
                } else {
                  closeDetailsRef.current = null;
                }
                // Only update SOAP state if explicitly provided (not the default false from overlay effect)
                // SOAP state updates come from onSOAPOpenChange callback, not from the overlay effect
                if (isSOAPOpen !== undefined || closeSOAPHandler !== undefined) {
                  setIsSOAPOpen(isSOAPOpen || false);
                  if (isSOAPOpen && closeSOAPHandler) {
                    closeSOAPRef.current = closeSOAPHandler;
                  } else {
                    closeSOAPRef.current = null;
                  }
                }
              }}
            />
          )}

          {activeTab === "inpatient" && (
            <InpatientTab
              clinicData={clinicData}
              clinicDataFromHook={clinicDataFromHook}
              userType={userType}
              isOwner={typeof isOwner === 'function' ? isOwner() : !!isOwner}
              vets={vets}
              loadingLists={allTabData.loading}
              inpatientVisits={allTabData.inpatientVisits}
              outpatientVisits={allTabData.outpatientVisits}
              autoOpenCreate={autoOpenCreate}
              onAutoOpenCreateConsumed={() => setAutoOpenCreate(false)}
              fetchAllTabData={fetchAllTabData}
              onDataUpdate={(newData) => {
                setAllTabData((prev) => ({
                  ...prev,
                  inpatientVisits: newData,
                }));
              }}
              onDetailsOpenChange={(isOpen, closeHandler, isSOAPOpen, closeSOAPHandler) => {
                setIsDetailsOpen(isOpen);
                if (isOpen && closeHandler) {
                  closeDetailsRef.current = closeHandler;
                } else {
                  closeDetailsRef.current = null;
                }
                // Only update SOAP state if explicitly provided (not the default false from overlay effect)
                // SOAP state updates come from onSOAPOpenChange callback, not from the overlay effect
                if (isSOAPOpen !== undefined || closeSOAPHandler !== undefined) {
                  setIsSOAPOpen(isSOAPOpen || false);
                  if (isSOAPOpen && closeSOAPHandler) {
                    closeSOAPRef.current = closeSOAPHandler;
                  } else {
                    closeSOAPRef.current = null;
                  }
                }
              }}
            />
          )}

          {activeTab === "billing" && (
            <Billing
              clinicData={clinicData}
              isOwner={typeof isOwner === 'function' ? isOwner() : !!isOwner}
            />
          )}

          {/* Fallback: If no tab matches, show loading */}
          {activeTab !== "appointments" && activeTab !== "emergency" && activeTab !== "outpatient" && activeTab !== "inpatient" && activeTab !== "billing" && (
            <div style={{ padding: "40px", textAlign: "center" }}>
              <p style={{ color: "#666", fontSize: "16px" }}>{t("reception.loading")}</p>
            </div>
          )}
        </main>
      </div>

      <PosInvoiceModal
        isOpen={showPosInvoiceModal}
        onClose={() => setShowPosInvoiceModal(false)}
        clinicData={clinicDataFromHook || clinicData}
      />
    </div>
  );
};

export default MainReceptionPage;
